import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Pagination, Checkbox } from 'antd'
import styled from 'styled-components'
import {
  fetchOrderedProducts,
  getAllOrderedProducts,
  setOrderedProductsPage,
} from 'src/features/OrderedProductsSlice'
import {
  getItemReceiptingPrice,
  getOrderedQuantity,
  getPrice,
  getQuantity,
  WEIGHT_UOM_PATTERN,
} from 'src/utils/priceFunctions'
import { VAT_RATE } from 'src/config'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import OrderedProductsCSV from './OrderedProductsCSV'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'

const Wrapper = styled.div`
  padding: 24px;
  background: #fff;
  .restrictShrink {
    white-space: nowrap;
  }
  /* Stacked layout: align with the full-width filter card above. */
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 0 0 24px;
  }
`

const Header = styled.h2`
  margin-top: 16px;
  margin-bottom: 0;
`

export const buildOrderedProductRows = (items, checkBoxName = 'All') => {
  let fileredItems = items

  if (checkBoxName === 'Receipted') {
    fileredItems = items.filter(
      item =>
        item.receipting &&
        item.receipting.status !== 'Not Saved' &&
        item.receipting.status !== undefined,
    )
  } else if (checkBoxName === 'Unreceipted') {
    fileredItems = items.filter(
      item =>
        !item.receipting ||
        item.receipting.status === 'Not Saved' ||
        item.receipting.status === undefined,
    )
  }

  const updatedItems = fileredItems
    .filter(
      item =>
        item.supplierConfirmed === true || item.supplierConfirmed === undefined,
    )
    .map(item => {
      const price = getPrice(item)
      const hasVat = item.hasVat || item.product?.hasVat || false
      // This is the Ordered Products screen: Qty shows what was
      // originally ordered. Received info is appended for discrepant
      // lines instead of replacing the ordered quantity with 0.
      const orderedQty = getOrderedQuantity(item)
      const receivedQty = getQuantity(item)
      const hasQtyDiscrepancy = item.receipting && receivedQty !== orderedQty
      const quantity = orderedQty
      // Received-basis money columns stay untouched (server totals and
      // CSV consumers rely on them).
      const netTotal = price * receivedQty || 0
      const grossTotal = getItemReceiptingPrice(item) || 0
      const totalVat = hasVat ? grossTotal - netTotal : 0
      return {
        ...item,
        price,
        hasVat,
        quantity,
        receivedQty,
        orderedUnitPrice: item.orderedUnitPrice ?? null,
        orderedValue: item.orderedValue ?? null,
        pricingUnit:
          [
            item.receipting?.billableUoM,
            item.UoM,
            item.pricedBy,
            item.product?.UoM,
            item.product?.pricedBy,
          ].find(
            unit => typeof unit === 'string' && WEIGHT_UOM_PATTERN.test(unit),
          ) ||
          item.UoM ||
          item.product?.UoM ||
          item.product?.pricedBy ||
          '',
        hasQtyDiscrepancy,
        netTotal: netTotal?.toFixed(2) || 0,
        grossTotal: Number(grossTotal)?.toFixed(2) || 0,
        totalVat: totalVat?.toFixed(2) || 0,
        supplierTitle:
          item.supplier?.title || item.product?.supplier?.title || 'N/A',
        product: {
          ...item.product,
          title: item.product?.title || 'N/A',
          hasVat: hasVat,
          size: item.product?.size || 'N/A',
        },
        key: item._id || item.id,
      }
    })
  return updatedItems
}

const OrderedProducts = () => {
  const blocked = useIsBlocked('ordered-products')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const dispatch = useDispatch()
  const { items, pagination, page, limit, calculatedTotals, isLoading, error } =
    useSelector(state => state.orderedProducts)
  const venueId = useSelector(state => state.venue.venue?._id)
  const filter = useSelector(state => state.filter.orders)
  const [dataSource, setDataSource] = useState([])
  const [checkBoxName, setCheckBoxName] = useState('All')

  useEffect(() => {
    dispatch(
      fetchOrderedProducts({
        venueId,
        start: filter?.startDate,
        end: filter?.endDate,
        page,
        limit,
        suppliers: filter?.suppliers,
        types: filter?.types,
      }),
    )
  }, [venueId, filter, page, limit, dispatch])

  useEffect(() => {
    if (items && items.length > 0) {
      const updatedItems = buildOrderedProductRows(items, checkBoxName)
      setDataSource(updatedItems)
    } else {
      setDataSource([])
    }
  }, [items, checkBoxName, pagination, calculatedTotals])

  const columns = [
    {
      title: 'Product',
      dataIndex: ['product', 'title'],
      key: 'productTitle',
      className: 'restrictShrink',
      sorter: (a, b) =>
        String(a.product.title)
          .trim()
          .localeCompare(String(b.product.title).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Ordered qty',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'restrictShrink',
      sorter: (a, b) => a.quantity - b.quantity,
      sortDirections: ['ascend', 'descend'],
      render: (quantity, item) =>
        item.hasQtyDiscrepancy
          ? `${quantity} (received ${item.receivedQty})`
          : quantity,
    },
    {
      title: 'Received pricing qty / weight',
      dataIndex: 'receivedQty',
      key: 'receivedQty',
      className: 'restrictShrink',
      render: (value, row) => `${value} ${row.pricingUnit}`.trim(),
    },
    {
      title: 'Size',
      dataIndex: ['product', 'size'],
      key: 'productSize',
      render: size => (size ? size : 'N/A'),
      className: 'restrictShrink',
      sorter: (a, b) =>
        String(a.product.size || '')
          .trim()
          .localeCompare(String(b.product.size || '').trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Unit price ex VAT',
      dataIndex: 'price',
      key: 'price',
      className: 'restrictShrink',
      render: (text, item) => {
        return `£${Number(text).toFixed(2) || 0}${
          item.product.hasVat ? ' + VAT' : ''
        }`
      },
      sorter: (a, b) =>
        (a.product.hasVat ? VAT_RATE * a.price : a.price) -
        (b.product.hasVat ? VAT_RATE * b.price : b.price),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplierTitle',
      className: 'restrictShrink',
      sorter: (a, b) => a.supplierName - b.supplierName,
      sortDirections: ['ascend', 'descend'],
    },

    {
      title: 'SKU',
      dataIndex: 'product.sku',
      key: 'productSku',
      className: 'restrictShrink',
      sorter: (a, b) =>
        String(a.product.sku || '')
          .trim()
          .localeCompare(String(b.product.sku || '').trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'VAT',
      dataIndex: 'totalVat',
      key: 'totalVat',
      className: 'restrictShrink',
      sorter: (a, b) => a.totalVat - b.totalVat,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Received spend ex VAT',
      dataIndex: 'netTotal',
      key: 'netTotal',
      className: 'restrictShrink',
      sorter: (a, b) => a.netTotal - b.netTotal,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Received spend inc VAT',
      dataIndex: 'grossTotal',
      key: 'grossTotal',
      className: 'restrictShrink',
      sorter: (a, b) => a.grossTotal - b.grossTotal,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: '',
      key: 'reportIssue',
      className: 'restrictShrink',
      width: 48,
      render: (_, item) => (
        <ReportProductIssueButton
          venueId={venueId}
          order={item}
          product={item.product}
          supplierTitle={item.supplierTitle}
          sourceScreen={PRODUCT_ISSUE_SOURCE.ORDERED_PRODUCTS}
          iconOnly
        />
      ),
    },
  ]

  const handlePageChange = newPage => {
    dispatch(setOrderedProductsPage(newPage))
  }

  return (
    <Wrapper>
      <Header>Ordered Products</Header>
      <p>
        Ordered value uses ordered quantities and prices. Received spend uses
        receipt quantities, weights and price adjustments where available;
        unreceipted lines use order values. These are not invoiced amounts.
        Dates filter when orders were placed. Missing historical prices may use
        current catalogue prices.
      </p>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          margin: '10px',
          marginLeft: '0px',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          <Checkbox
            checked={checkBoxName === 'All'}
            onChange={() => setCheckBoxName('All')}
          >
            All
          </Checkbox>
          <Checkbox
            checked={checkBoxName === 'Receipted'}
            onChange={() => setCheckBoxName('Receipted')}
          >
            Receipted
          </Checkbox>
          <Checkbox
            checked={checkBoxName === 'Unreceipted'}
            onChange={() => setCheckBoxName('Unreceipted')}
          >
            Unreceipted
          </Checkbox>
        </div>
        {!!pagination.totalOrders && (
          <div>
            <OrderedProductsCSV
              loadData={async () =>
                buildOrderedProductRows(
                  await getAllOrderedProducts({
                    venueId,
                    start: filter.startDate,
                    end: filter.endDate,
                    suppliers: filter.suppliers,
                    types: filter.types,
                  }),
                  checkBoxName,
                )
              }
              disabled={isLoading}
            />{' '}
          </div>
        )}
      </div>

      <Pagination
        style={{ marginBottom: 16, textAlign: 'right' }}
        current={page}
        pageSize={limit}
        total={pagination.totalOrders}
        onChange={handlePageChange}
      />
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        pagination={false}
        bordered
        scroll={RESPONSIVE_TABLE_SCROLL}
      />
      <div
        style={{
          marginBottom: 16,
          marginTop: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          textAlign: 'right',
        }}
      >
        <span>
          <strong>Ordered Value ex VAT: </strong> £
          {Number(calculatedTotals.totalSpendOrderedNoVat || 0).toFixed(2)}
        </span>
        <span>
          <strong>Received Spend ex VAT: </strong> £
          {Number(calculatedTotals.totalSpenReceivedNoVat || 0).toFixed(2)}
        </span>
        <span>
          <strong>Received VAT: </strong> £
          {Number(calculatedTotals.totalVATAmount || 0).toFixed(2)}
        </span>
        <span>
          <strong>Ordered Value inc VAT:</strong> £
          {Number(calculatedTotals.totalSpendOrderedVat || 0).toFixed(2)}
        </span>
        <span>
          <strong>Received Spend inc VAT:</strong> £
          {Number(calculatedTotals.totalSpendReceivedVat || 0).toFixed(2)}
        </span>
      </div>
    </Wrapper>
  )
}

export default OrderedProducts
